import { createServerFn } from "@tanstack/react-start";

import {
	authMiddleware,
	mondayBoards,
	userServices,
} from "@onesolution/middlewares";

import { mondayClient } from "@onesolution/monday/monday";
import {
	OperationFiltersParams,
	makeOperationsFilters,
} from "@onesolution/utils/makeOperationFilters";
import { endOfDay, startOfDay } from "date-fns";
import { wrapWithUseQuery } from "..";

export function getTimelineRange(datesString: string) {
	const dates = datesString.split(", ").flatMap((s) => s.split(" - "));
	const validDates = dates.filter(Boolean).map((d) => new Date(d!)) as Date[];

	if (validDates.length === 0) {
		return {
			start: new Date(Date.now()).toISOString(),
			end: new Date(Date.now()).toISOString(),
		};
	}

	const start = startOfDay(
		new Date(Math.min(...validDates.map((d) => d.getTime()))),
	).toISOString();
	const end = endOfDay(
		new Date(Math.max(...validDates.map((d) => d.getTime()))),
	).toISOString();

	return { start, end };
}

const handler = createServerFn()
	.middleware([authMiddleware, mondayBoards, userServices])
	.validator(OperationFiltersParams)
	.handler(async ({ context, data }) => {
		const { items: operations, cursor } =
			await mondayClient.query.operationPlanningBase(context, {
				cursor: data.cursor,
				queryParams: await makeOperationsFilters(context, data),
			});

		return {
			cursor,
			items: operations.map((op) => {
				return {
					id: Number(op.id),
					text: op.name,
					...getTimelineRange(op.timeline),
					type: op.status.label?.replace(/\s+/g, "").toLocaleLowerCase(),
					status: op.status,
					parent: 0,
					subIds: op.products.linked_item_ids,
					lazy: !!op.products.linked_item_ids.length,
				};
			}),
		};
	});

export const getOperationPlanningBaseFromServer = wrapWithUseQuery(
	"operations-planning-base",
	handler,
);

export type OperationPlanningBase = Awaited<
	ReturnType<typeof getOperationPlanningBaseFromServer>
>["items"];
